/**
 * Small monochrome line glyphs for the chart context menu. 15px,
 * stroke = currentColor so they inherit the row's (dimmed) color and the
 * hover/danger states for free. Extracted from Chart.tsx.
 */
import type { ReactNode } from "react";

export type MenuGlyphName =
  | "jump"
  | "trading"
  | "view"
  | "image"
  | "properties"
  | "oneclick"
  | "buy"
  | "sell"
  | "pending"
  | "running"
  | "sltp"
  | "history"
  | "copy"
  | "save"
  | "panel";

export function MenuGlyph({ name }: { name: MenuGlyphName }) {
  const p = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const paths: Record<MenuGlyphName, ReactNode> = {
    jump: (
      <>
        <path d="M3 4 L8 7.5 L3 11 Z" {...p} />
        <path d="M8 4 L12 7.5 L8 11 Z" {...p} />
      </>
    ),
    trading: (
      <>
        <line x1="4.5" y1="2.5" x2="4.5" y2="12.5" {...p} />
        <rect x="3" y="5" width="3" height="4" {...p} />
        <line x1="10.5" y1="3.5" x2="10.5" y2="13.5" {...p} />
        <rect x="9" y="6" width="3" height="4.5" {...p} />
      </>
    ),
    view: (
      <>
        <path d="M1.5 7.5 C3.5 4, 10.5 4, 12.5 7.5 C10.5 11, 3.5 11, 1.5 7.5 Z" {...p} />
        <circle cx="7" cy="7.5" r="1.6" {...p} />
      </>
    ),
    image: (
      <>
        <rect x="2" y="3" width="11" height="9" rx="1.5" {...p} />
        <circle cx="5.5" cy="6" r="1" {...p} />
        <path d="M3 11 L6 8 L8.5 10 L10 8.5 L12.5 11" {...p} />
      </>
    ),
    properties: (
      <>
        <line x1="3" y1="4.5" x2="12" y2="4.5" {...p} />
        <line x1="3" y1="10.5" x2="12" y2="10.5" {...p} />
        <circle cx="6" cy="4.5" r="1.6" {...p} />
        <circle cx="9.5" cy="10.5" r="1.6" {...p} />
      </>
    ),
    oneclick: <path d="M8 1.5 L4 8 H7 L6.5 13.5 L11 6.5 H7.5 Z" {...p} />,
    buy: (
      <>
        <line x1="7.5" y1="12" x2="7.5" y2="3" {...p} />
        <path d="M4 6.5 L7.5 3 L11 6.5" {...p} />
      </>
    ),
    sell: (
      <>
        <line x1="7.5" y1="3" x2="7.5" y2="12" {...p} />
        <path d="M4 8.5 L7.5 12 L11 8.5" {...p} />
      </>
    ),
    pending: (
      <>
        <circle cx="7.5" cy="7.5" r="5.2" {...p} />
        <path d="M7.5 4.5 V7.7 L9.7 9" {...p} />
      </>
    ),
    running: <path d="M1.5 8 H4 L5.5 4 L8 11 L9.5 8 H13" {...p} />,
    sltp: (
      <>
        <circle cx="7.5" cy="7.5" r="5" {...p} />
        <circle cx="7.5" cy="7.5" r="1.4" {...p} />
        <line x1="7.5" y1="0.8" x2="7.5" y2="3" {...p} />
        <line x1="7.5" y1="12" x2="7.5" y2="14.2" {...p} />
      </>
    ),
    history: (
      <>
        <path d="M2.5 7.5 A5 5 0 1 0 4 4" {...p} />
        <path d="M2 2.5 V4.5 H4" {...p} />
        <path d="M7.5 5 V7.7 L9.5 9" {...p} />
      </>
    ),
    copy: (
      <>
        <rect x="5" y="5" width="8" height="8" rx="1.3" {...p} />
        <path d="M3.5 9.5 H2.5 V2.5 H9.5 V3.5" {...p} />
      </>
    ),
    save: (
      <>
        <line x1="7.5" y1="2.5" x2="7.5" y2="9.5" {...p} />
        <path d="M4.5 6.5 L7.5 9.5 L10.5 6.5" {...p} />
        <path d="M2.5 11.5 H12.5" {...p} />
      </>
    ),
    panel: (
      <>
        <rect x="2" y="2.5" width="11" height="10" rx="1.5" {...p} />
        <line x1="2" y1="5.5" x2="13" y2="5.5" {...p} />
        <line x1="4" y1="8" x2="11" y2="8" {...p} />
        <line x1="4" y1="10.3" x2="11" y2="10.3" {...p} />
      </>
    ),
  };
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      aria-hidden
      style={{ display: "block" }}
    >
      {paths[name]}
    </svg>
  );
}
